<template>
  <v-layout id="top-bar" align-center class="pa-0">
    <v-flex grow class="pl-3">
      <v-layout align-center class="gap">
        <v-flex shrink class="pointer pr-4" @click="goHome">
          <v-layout shrink pt-1>
            <v-flex class="pr-3">
              <paradigm-logo style="height: 36px" />
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex shrink v-if="showPatientNav">
          <v-btn
            v-if="showPatientNav"
            dark
            text
            :href="typeof patientNavLink === 'string' ? patientNavLink : undefined"
            :to="typeof patientNavLink === 'string' ? undefined : patientNavLink"
            data-e2e-id="patients-home-menu-link"
            >Patients
          </v-btn>
        </v-flex>
        <v-flex shrink v-if="showTrialsNav">
          <v-layout>
            <v-flex shrink>
              <v-badge overlap color="rgba(0, 0, 0, 0)">
                <template #badge>
                  <trial-notification-bubble :badge="trialNotificationCount" />
                </template>
                <v-btn
                  dark
                  text
                  :href="typeof trialsNavLink === 'string' ? trialsNavLink : undefined"
                  :to="typeof trialsNavLink === 'string' ? undefined : trialsNavLink"
                  data-e2e-id="trials-home-menu-link"
                  >Trials
                </v-btn>
              </v-badge>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex shrink v-if="showInsightsNav">
          <v-btn
            dark
            text
            :to="{ name: 'insights', params: { orgId: orgId } }"
            data-e2e-id="insights-home-menu-link"
            >Insights
          </v-btn>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex class="pr-5" shrink>
      <global-add-bar />
    </v-flex>
    <v-flex class="pr-5 mt-3" shrink>
      <search-box />
    </v-flex>
    <v-flex v-if="showNotifications" shrink class="pr-3">
      <app-reminder-notification-container />
    </v-flex>
    <v-flex shrink>
      <the-top-bar-more-dropdown />
    </v-flex>
    <v-flex shrink>
      <the-user-dropdown />
    </v-flex>
  </v-layout>
</template>

<script>
import GlobalAddBar from '@/components/layout/GlobalAddBar'
import SearchBox from '@/components/search/SearchBox'
import TheTopBarMoreDropdown from '@/components/layout/TheTopBarMoreDropdown'
import TheUserDropdown from '@/components/layout/TheUserDropdown'
import TrialNotificationBubble from '@/components/trials/TrialNotificationBubble'
import ParadigmLogo from '@/assets/svg/ParadigmLogo'
import { MyActiveOrganization, MyActivePermissions } from '@/helpers/my-account-helper'
import { permissionsMixin } from '@/mixins/permissions'
import { mapState } from 'vuex'
import { isFeatureOn } from '@/helpers/feature-flag-helper'
import _ from 'lodash'
import { GET_UNREAD_COUNTS } from '@/store/mutation-types'

export default {
  components: {
    GlobalAddBar,
    SearchBox,
    TheTopBarMoreDropdown,
    TheUserDropdown,
    TrialNotificationBubble,
    ParadigmLogo
  },
  mixins: [permissionsMixin],
  data() {
    return {
      orgId: MyActiveOrganization.id
    }
  },
  computed: {
    ...mapState({
      trialNotificationPreferences: state => state.trialNotificationPreferences,
      refreshUserNotifications: state => state.refreshUserNotifications,
      unreadCounts: state => state.unreadCounts
    }),
    showNotifications() {
      return isFeatureOn('trials')
    },
    showTrialsNav() {
      return isFeatureOn('trials') && this.hasAnyPermission('TRIAL_READ')
    },
    showInsightsNav() {
      return this.hasAnyPermission(['INSIGHTS_READ'])
    },
    showPatientNav() {
      return this.hasAnyPermission(['PATIENT_READ'])
    },
    patientNavLink() {
      return `${window.$appConfig.VUE_APP_PLATFORM_UI_URL}/org/${this.orgId}/patients`
    },
    trialsNavLink() {
      if (isFeatureOn('viper-platform-ui-react-trials-page')) {
        return `${window.$appConfig.VUE_APP_PLATFORM_UI_URL}/org/${this.orgId}/trials`
      }
      return {
        name: 'trials',
        params: { orgId: this.orgId }
      }
    },
    internalNotificationPrefs() {
      return _.chain(this.trialNotificationPreferences)
        .keyBy('trial')
        .mapValues('internalNotifications')
        .value()
    },
    trialNotificationCount() {
      const subscribedTrialIds = _.filter(
        _.keys(this.internalNotificationPrefs),
        trialId => this.internalNotificationPrefs[trialId]
      )
      const subscribedUnreadCounts = _.filter(this.unreadCounts, it =>
        _.includes(subscribedTrialIds, it.trialId)
      )
      return _.sumBy(subscribedUnreadCounts, 'unreadCount')
    }
  },
  watch: {
    trialNotificationPreferences: {
      // this is necessary since notifications are filtered by the API
      handler(newValue, oldValue) {
        if (!_.isEmpty(oldValue)) {
          this.fetchNotifications()
        }
      },
      deep: true
    },
    refreshUserNotifications() {
      this.fetchUnreadCounts()
    }
  },
  created() {
    this.fetchNotifications()
  },
  methods: {
    goHome() {
      this.$router.push({ name: 'home' })
    },
    async fetchNotifications() {
      if (_.includes(MyActivePermissions, 'TRIAL_READ')) {
        await this.fetchUnreadCounts()
      }
    },
    async fetchUnreadCounts() {
      await this.$store.dispatch(GET_UNREAD_COUNTS)
    }
  }
}
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}

/* stylelint-disable-next-line selector-pseudo-element-no-unknown */
::v-deep .v-badge__badge {
  left: calc(100% - 20px) !important;
}

.gap {
  gap: 8px;
}
</style>
